/**
 * Manual verification harness for the dynamic-game-loading pilot: proves a
 * game's client bundle can be loaded via runtime import() (not a static
 * workspace import) and rendered inside the real running app, sharing its
 * React instance rather than duplicating it. Reached only via the
 * /dev/dynamic-game-pilot path — not part of any production flow, and
 * deliberately does not statically import anything from @merels/games.
 */
import { useEffect, useState } from 'react'
import { makeDynamicGameLoader } from '../deps/catalog/dynamicGameLoader/dynamicGameLoader'
import type { LoadedDynamicGame } from '../deps/catalog/dynamicGameLoader/dynamicGameLoader.types'

const EMPTY_BOARD_STATE = { positionValues: {} }

interface DynamicGamePilotProps {
  workerUrl: string
}

export function DynamicGamePilot({ workerUrl }: DynamicGamePilotProps) {
  const [loaded, setLoaded] = useState<LoadedDynamicGame | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [lastMove, setLastMove] = useState<string | null>(null)

  useEffect(() => {
    const loader = makeDynamicGameLoader(workerUrl)
    loader
      .loadGameClient('tictactoe')
      .then(setLoaded)
      .catch((err) => setError(String(err)))
  }, [workerUrl])

  if (error) {
    return <div style={{ padding: 24, color: 'red', fontFamily: 'monospace' }}>Dynamic load failed: {error}</div>
  }

  if (!loaded) {
    return <div style={{ padding: 24, fontFamily: 'monospace' }}>Loading tictactoe dynamically…</div>
  }

  const { Game } = loaded

  return (
    <div style={{ padding: 24, fontFamily: 'sans-serif' }}>
      <h1>Dynamic Game Pilot: tictactoe</h1>
      <p>Loaded via runtime import() from a published bundle, not a static workspace import.</p>
      {lastMove && <p style={{ fontFamily: 'monospace' }}>Last submitted move: {lastMove}</p>}
      <div style={{ maxWidth: 480, height: 480 }}>
        <Game
          gameState={EMPTY_BOARD_STATE}
          ctx={{ currentPlayer: '0', turn: 1 }}
          playerConfiguration={{ mode: 'single', isMyTurn: true }}
          submitMove={(move: unknown, sourceId: string) => setLastMove(`${JSON.stringify(move)} (${sourceId})`)}
        />
      </div>
    </div>
  )
}
